import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { Box, Button, ButtonGroup, Spinner, Textarea } from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown'
import { useIsMobile } from '../../hooks/useIsMobile'
import { proseContentCss } from '../../theme/proseStyles'

const RichMarkdownEditor = lazy(() =>
  import('./RichMarkdownEditor').then((m) => ({ default: m.RichMarkdownEditor })),
)

/** Insert-at-cursor seam handed to the caller once the underlying editor (textarea or Tiptap) is
 * ready -- lets a merge-field picker splice a token in without the editor knowing about it. */
export interface MarkdownEditorApi {
  insertAtCursor: (text: string) => void
}

interface MarkdownEditorProps {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  rows?: number
  onReady?: (api: MarkdownEditorApi) => void
  /** Forwarded to the desktop rich editor's toolbar. Default true; false hides "Import document"
   *  (.docx / .odt) where an imported doc is never the source (Lobby Day logistics — LW-14). */
  allowWordImport?: boolean
}

/** Desktop → Tiptap WYSIWYG + Word import (lazy). Mobile → Markdown textarea. */
export function MarkdownEditor({ value, onChange, placeholder, rows = 8, onReady, allowWordImport }: MarkdownEditorProps) {
  const isMobile = useIsMobile()
  const [mode, setMode] = useState<'write' | 'preview'>('write')
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const onReadyCalledRef = useRef(false)
  // insertAtCursor is created once (below) but must always act on the LATEST value/onChange, not
  // whatever was in scope the render onReady fired -- keep them in refs, updated every render,
  // rather than closing over the props directly (which would go stale after the first edit).
  const valueRef = useRef(value)
  valueRef.current = value
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  useEffect(() => {
    if (isMobile && !onReadyCalledRef.current && onReady) {
      onReadyCalledRef.current = true
      onReady({
        insertAtCursor: (text: string) => {
          const el = textareaRef.current
          // No live textarea (e.g. currently in Preview mode) -- fall back to appending.
          if (!el) {
            onChangeRef.current(valueRef.current + text)
            return
          }
          const start = el.selectionStart ?? el.value.length
          const end = el.selectionEnd ?? el.value.length
          const next = el.value.slice(0, start) + text + el.value.slice(end)
          onChangeRef.current(next)
        },
      })
    }
    // Intentionally only re-runs on isMobile flip: onReady itself must fire exactly once, and
    // value/onChange churn is already covered via the refs above rather than re-arming this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile])

  if (!isMobile) {
    return (
      <Suspense fallback={<Spinner size="sm" />}>
        <RichMarkdownEditor value={value} onChange={onChange} placeholder={placeholder} rows={rows} onReady={onReady} allowWordImport={allowWordImport} />
      </Suspense>
    )
  }

  return (
    <Box>
      <ButtonGroup size="xs" attached mb={2}>
        <Button variant={mode === 'write' ? 'solid' : 'outline'} onClick={() => setMode('write')}>Write</Button>
        <Button variant={mode === 'preview' ? 'solid' : 'outline'} onClick={() => setMode('preview')}>Preview</Button>
      </ButtonGroup>
      {mode === 'write' ? (
        <Textarea ref={textareaRef} value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} rows={rows} fontFamily="mono" fontSize="sm" />
      ) : (
        <Box borderWidth="1px" borderRadius="md" p={3} minH={`${rows * 1.5}rem`}
          css={proseContentCss}>
          <ReactMarkdown>{value || '*Nothing yet*'}</ReactMarkdown>
        </Box>
      )}
    </Box>
  );
}
